import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as _ from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class RepointTabService {
  constructor(private http: HttpClient) {}

  saveTb(baseUrl: string, jsonSettings: any, accounts: any[], sourceSystem: string, globalParams: any, filterParams: any): Observable<any> {
    // Match legacy AngularJS (trailBalanceService.js:99-114): per-source action_code + TB_SAVE process.
    // The previous `u7kuxv` action mapped to PKG_GT_FIR_TRIAL_BALANCE.SAVE_FIR_REPOINT_DATA on the backend
    // and returned NO_DATA_FOUND for DCS rows.
    const actionCode = sourceSystem === 'FIR' ? 'iv9fni' : 'glr5xa';
    const url = `${baseUrl}/saveJsonObject?action_code=${actionCode}`;
    const params: any = _.merge({}, globalParams, filterParams);
    params.jsonObj = JSON.stringify(accounts);
    params.jsonSettings = JSON.stringify(jsonSettings);
    params.process_name = 'TB_SAVE';

    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        urlEncoded.set(key, params[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  fetchSystemAcctData(
    baseUrl: string,
    taxYear: string,
    localAcct: string,
    sysAcct: string,
    sourceSystem: string,
    globalParams: any
  ): Observable<any> {
    const params: any = _.merge({}, globalParams, {
      action_code: sourceSystem === 'FIR' ? '4fmxwf' : '5hsimn',
      tax_year: taxYear,
      local_acct: localAcct,
      sys_acct: sysAcct
    });
    return this.http.get<any>(`${baseUrl}/loadJsonObject`, { params, withCredentials: true });
  }

  getDefaultSystemAcctValues(
    baseUrl: string,
    scenario: string,
    jcdKey: string | number,
    taxYear: string,
    sourceSystem: string,
    combinationKey: string | number,
    localAcct: string,
    localChartKey: string | number,
    screenKey: string | number,
    globalParams: any,
    filterParams: any
  ): Observable<any> {
    const params: any = _.merge({}, globalParams, filterParams, {
      action_code: sourceSystem === 'FIR' ? 'uoezbr' : '4cpmap',
      scenario,
      jcd_key: jcdKey,
      tax_year: taxYear,
      source_sys: sourceSystem,
      combination_key: combinationKey,
      local_acct: localAcct,
      local_chart_key: localChartKey,
      screen_key: screenKey || filterParams?.screen_key || globalParams?.screen_key || '',
      screen_type: filterParams?.screen_type || globalParams?.screen_type || 'grid',
      activity_key: filterParams?.activity_key || globalParams?.activity_key || '',
      filing_key: filterParams?.filing_key || globalParams?.filing_key || 1,
      filter_by: filterParams?.filter_by || globalParams?.filter_by || 9,
      is_locked: filterParams?.is_locked || globalParams?.is_locked || 'N',
      issue_id: filterParams?.issue_id || globalParams?.issue_id || '',
      issue_id_for_tx_save: filterParams?.issue_id_for_tx_save || globalParams?.issue_id_for_tx_save || '',
      issue_short_desc: filterParams?.issue_short_desc || globalParams?.issue_short_desc || '',
      jcd_ta_key: filterParams?.jcd_ta_key || globalParams?.jcd_ta_key || '',
      parent_me_string: filterParams?.parent_me_string || globalParams?.parent_me_string || '',
      scenarioCode: filterParams?.scenarioCode || filterParams?.scenario_code || globalParams?.scenarioCode || 'RAF',
      is_issue_key_enabled: 'N'
    });
    return this.http.get<any>(`${baseUrl}/loadJsonObject`, { params, withCredentials: true });
  }

  getSystmAcctDropDown(
    baseUrl: string,
    screenKey: string | number,
    acctType: string | number,
    sourceSystem: string,
    sysAcctKey: string | number,
    globalParams: any,
    filterParams: any
  ): Observable<any> {
    // Match legacy AngularJS (trialBalanceController.js:1676): populates the System Account
    // dropdown with SYS_DROPDOWN strings. The other lookup (zdfsb6/aenjj8) returns a different
    // shape and lacks SYS_DROPDOWN, so it cannot back the dropdown.
    const params: any = _.merge({}, globalParams, filterParams, {
      action_code: sourceSystem === 'FIR' ? 'cftzzy' : 'qsbz14',
      screen_key: screenKey,
      acct_type: acctType,
      source_system: sourceSystem,
      sys_acct_key: sysAcctKey
    });
    return this.http.get<any>(`${baseUrl}/loadJsonObject`, { params, withCredentials: true });
  }

  getTradingPartners(
    baseUrl: string,
    taxYear: string | number,
    scenario: string | number,
    searchString: string,
    sourceSystem: string,
    globalParams: any
  ): Observable<any> {
    // Legacy always uses 50swfi for typeahead search (DCS sckfcq dropdown was commented out).
    const params: any = _.merge({}, globalParams, {
      action_code: '50swfi',
      tax_year: taxYear,
      scenario,
      search_string: searchString
    });
    return this.http.get<any>(`${baseUrl}/loadJsonObject`, { params, withCredentials: true });
  }

  getSystemAcctLookup(
    baseUrl: string,
    screenKey: string | number,
    acctType: string | number,
    sourceSystem: string,
    globalParams: any,
    filterParams: any,
    rowData: any
  ): Observable<any> {
    const params: any = _.merge({}, globalParams, filterParams, {
      action_code: sourceSystem === 'FIR' ? 'zdfsb6' : 'aenjj8',
      acct_type: acctType,
      activity_key: filterParams?.activity_key || globalParams?.activity_key || rowData?.ACTIVITY_KEY || '',
      filing_key: filterParams?.filing_key || globalParams?.filing_key || rowData?.CONSOL_GROUP_KEY || 1,
      filter_by: filterParams?.filter_by || globalParams?.filter_by || 9,
      is_issue_key_enabled: filterParams?.is_issue_key_enabled || globalParams?.is_issue_key_enabled || 'N',
      is_locked: filterParams?.is_locked || globalParams?.is_locked || 'N',
      issue_id: filterParams?.issue_id || globalParams?.issue_id || '',
      issue_id_for_tx_save: filterParams?.issue_id_for_tx_save || globalParams?.issue_id_for_tx_save || '',
      issue_short_desc: filterParams?.issue_short_desc || globalParams?.issue_short_desc || '',
      jcd_key: rowData?.JCD_KEY || filterParams?.jcd_key || globalParams?.jcd_key || 250,
      jcd_ta_key: filterParams?.jcd_ta_key || globalParams?.jcd_ta_key || '',
      parent_me_string: filterParams?.parent_me_string || globalParams?.parent_me_string || '',
      scenario: rowData?.SCENARIO || filterParams?.scenario || globalParams?.scenario || 74,
      scenarioCode: filterParams?.scenarioCode || filterParams?.scenario_code || globalParams?.scenarioCode || 'RAF',
      screen_key: screenKey || rowData?.SCREEN_KEY || filterParams?.screen_key || globalParams?.screen_key || 175,
      screen_type: filterParams?.screen_type || globalParams?.screen_type || 'grid',
      tax_year: rowData?.TAX_YEAR || filterParams?.tax_year || globalParams?.tax_year || ''
    });
    return this.http.get<any>(`${baseUrl}/loadJsonObject`, { params, withCredentials: true });
  }
}

